import React, { useState, useCallback, useEffect } from "react";
import { X, Envelope, Lock, User, ArrowRight, Sparkle, Warning } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { startGoogleSignIn } from "../lib/capacitorAuth";

// FastAPI 422 validation returns detail as an array of {msg,loc,...} objects.
// Coerce every shape into a plain string so React doesn't crash trying to
// render an object as a child.
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

/**
 * PortalAuthModal — email + password auth for AstroKuberChat.
 *
 * Second login method beside Google Sign-In. Ships with the iOS submission
 * both to satisfy Apple Guideline 4.8 (equivalent alt auth) and to give
 * App Store reviewers a path that bypasses Google's "unusual sign-in"
 * 2FA blocker.
 *
 * Three modes cycled via internal state:
 *   • "login"  — email + password → /api/auth/email-login
 *   • "signup" — email + password + name → /api/auth/email-signup
 *   • "forgot" — email → /api/auth/email-password-reset-request
 */
export default function PortalAuthModal({ open, onClose, initialMode = "login", onSuccess }) {
  const { setUser } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Reset ephemeral state whenever the modal is (re-)opened.
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setEmail(""); setPassword(""); setName("");
      setBusy(false); setError(""); setInfo("");
    }
  }, [open, initialMode]);

  // Close on ESC.
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(""); setInfo(""); setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/email-login", { email: email.trim().toLowerCase(), password });
        if (data?.user) setUser(data.user);
        onSuccess?.(data);
        onClose?.();
      } else if (mode === "signup") {
        const { data } = await api.post("/auth/email-signup", {
          email: email.trim().toLowerCase(),
          password,
          name: (name || "").trim() || undefined,
        });
        if (data?.user) setUser(data.user);
        onSuccess?.(data);
        onClose?.();
      } else if (mode === "forgot") {
        await api.post("/auth/email-password-reset-request", { email: email.trim().toLowerCase() });
        setInfo("If an account exists for this email, we've sent a reset link. Check your inbox (and spam) in the next minute.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }, [mode, email, password, name, setUser, onSuccess, onClose]);

  if (!open) return null;

  const title = mode === "login" ? "Login via Portal"
              : mode === "signup" ? "Create your Portal account"
              : "Reset your password";
  const subtitle = mode === "login" ? "Sign in with the email and password you set on AstroKuberChat."
                 : mode === "signup" ? "One less thing to remember — no Google account needed."
                 : "We'll email you a reset link that works for the next hour.";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="portal-auth-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-surface text-foreground rounded-3xl shadow-2xl ring-1 ring-amber-300/40 overflow-hidden relative"
      >
        {/* gold hairline top */}
        <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#F6C97C] via-[#E89E4B] to-[#C58F3B]" />

        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition"
          aria-label="Close"
          data-testid="portal-auth-close"
        >
          <X size={20} weight="bold" />
        </button>

        <div className="px-6 pt-8 pb-3">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[.14em] bg-gradient-to-br from-[#FDE1B4] to-[#F6C97C] text-[#3F2D1B]">
            <Sparkle size={10} weight="fill" /> SIGN IN
          </div>
          <h2 className="mt-3 font-serif-display text-2xl leading-tight">{title}</h2>
          <p className="mt-1 text-sm text-muted">{subtitle}</p>
        </div>

        {/* Google button — always co-located with the Portal form so users
            never have to guess where the other auth method lives. */}
        {mode !== "forgot" && (
          <div className="px-6 pb-2">
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + "/astrologers";
                startGoogleSignIn(redirectUrl);
              }}
              className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#1A1410] font-semibold text-sm rounded-xl px-4 py-2.5 ring-1 ring-black/10 hover:bg-white/90 transition"
              data-testid="portal-auth-google"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09A6.977 6.977 0 015.48 12c0-.73.13-1.44.36-2.09V7.07H2.18A11 11 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              Login Via Google
            </button>
            <div className="my-3 flex items-center gap-3 text-[11px] uppercase tracking-widest text-muted font-semibold">
              <span className="flex-1 h-px bg-soft" />
              or via Portal
              <span className="flex-1 h-px bg-soft" />
            </div>
          </div>
        )}

        <div className="px-6 pb-6 space-y-3">
          {mode === "signup" && (
            <div>
              <label className="text-[11px] font-semibold text-muted uppercase tracking-wide">Your name</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-soft bg-bg px-3 py-2.5 focus-within:ring-2 focus-within:ring-amber-400/60">
                <User size={16} className="text-muted flex-shrink-0" />
                <input
                  type="text"
                  className="flex-1 bg-transparent outline-none text-sm"
                  placeholder="e.g. Ravi Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  maxLength={80}
                  data-testid="portal-auth-name"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-[11px] font-semibold text-muted uppercase tracking-wide">Email</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-soft bg-bg px-3 py-2.5 focus-within:ring-2 focus-within:ring-amber-400/60">
              <Envelope size={16} className="text-muted flex-shrink-0" />
              <input
                type="email"
                className="flex-1 bg-transparent outline-none text-sm"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                data-testid="portal-auth-email"
              />
            </div>
          </div>

          {mode !== "forgot" && (
            <div>
              <label className="text-[11px] font-semibold text-muted uppercase tracking-wide">Password</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-soft bg-bg px-3 py-2.5 focus-within:ring-2 focus-within:ring-amber-400/60">
                <Lock size={16} className="text-muted flex-shrink-0" />
                <input
                  type="password"
                  className="flex-1 bg-transparent outline-none text-sm"
                  placeholder={mode === "signup" ? "Min 8 chars · letters + numbers" : "Your password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  required
                  minLength={8}
                  data-testid="portal-auth-password"
                />
              </div>
              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => { setError(""); setInfo(""); setMode("forgot"); }}
                  className="mt-1.5 text-xs text-gold hover:underline"
                  data-testid="portal-auth-forgot-link"
                >
                  Forgot password?
                </button>
              )}
            </div>
          )}

          {error && (
            <div
              className="flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-3 py-2 text-[13px]"
              data-testid="portal-auth-error"
            >
              <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {info && (
            <div
              className="flex items-start gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 px-3 py-2 text-[13px]"
              data-testid="portal-auth-info"
            >
              <Sparkle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
              <span>{info}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl px-4 py-3 shadow-[0_6px_16px_rgba(22,163,74,.35)] transition-all"
            data-testid="portal-auth-submit"
          >
            {busy ? (
              <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" aria-hidden />
            ) : (
              <>
                {mode === "login" ? "Log in"
                 : mode === "signup" ? "Create account"
                 : "Send reset link"}
                <ArrowRight size={14} weight="bold" />
              </>
            )}
          </button>

          <div className="pt-1 text-center text-xs text-muted">
            {mode === "login" && (
              <>New to AstroKuberChat?{" "}
                <button
                  type="button"
                  onClick={() => { setError(""); setInfo(""); setMode("signup"); }}
                  className="text-gold font-semibold hover:underline"
                  data-testid="portal-auth-switch-signup"
                >
                  Create an account
                </button>
              </>
            )}
            {mode === "signup" && (
              <>Already have a Portal account?{" "}
                <button
                  type="button"
                  onClick={() => { setError(""); setInfo(""); setMode("login"); }}
                  className="text-gold font-semibold hover:underline"
                  data-testid="portal-auth-switch-login"
                >
                  Log in
                </button>
              </>
            )}
            {mode === "forgot" && (
              <button
                type="button"
                onClick={() => { setError(""); setInfo(""); setMode("login"); }}
                className="text-gold font-semibold hover:underline"
                data-testid="portal-auth-back-login"
              >
                ← Back to log in
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
